import { CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";

import { ENLIGHTEN_APP_SUBPATH } from "./common/constants";
import { getPostHogConfig, POSTHOG_API_KEY, PostHogProvider, PostHogUserIdentifier } from "./common/posthog";
import { theme } from "./common/theme/theme";
import { ErrorBoundary, RouteErrorBoundary } from "./components/ui/ErrorBoundary";
import AccountSettings from "./features/account/AccountSettings";
import { AccountAnalysisPage } from "./features/account-analysis";
import { AuthProvider } from "./features/auth/AuthContext";
import Login from "./features/auth/Login";
import OtpVerification from "./features/auth/OtpVerification";
import { ProtectedRoute } from "./features/auth/ProtectedRoute";
import ResetPassword from "./features/auth/ResetPassword";
import ResetPasswordConfirmation from "./features/auth/ResetPasswordConfirmation";
import AhaMomentsHub from "./features/coaching/AhaMomentsHub";
import CoachingArtifactDetail from "./features/coaching/CoachingArtifactDetail";
import Dashboard from "./features/dashboard/Dashboard";
import { DatasetDetail } from "./features/dataset-management/DatasetDetail";
import { DatasetManagement } from "./features/dataset-management/DatasetManagement";
import { Explore } from "./features/explore/Explore";
import { AppLayout } from "./features/layout/AppLayout";
import { LayoutProvider } from "./features/layout/LayoutContext";
import LensLibrary from "./features/lens-library/LensLibrary";
import Performance from "./features/performance/Performance";
import PerformanceAnalysis from "./features/performance-analysis/PerformanceAnalysis";
import AITransparency from "./features/static-pages/AITransparency";
import Viewer from "./features/viewer/Viewer";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0, //5 * 60 * 1000,   // optional defaults
      refetchOnWindowFocus: false
    }
  }
});

const basename = window.location.pathname.startsWith(ENLIGHTEN_APP_SUBPATH) ? ENLIGHTEN_APP_SUBPATH : "/";

export const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <PostHogProvider apiKey={POSTHOG_API_KEY} options={getPostHogConfig()}>
              <PostHogUserIdentifier>
                <LayoutProvider>
                  <Router basename={basename}>
                    <Routes>
                      <Route path="/login" element={<Login />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/verify-otp" element={<OtpVerification />} />
                      <Route path="/reset-password-confirmation" element={<ResetPasswordConfirmation />} />
                      <Route
                        path="/*"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <RouteErrorBoundary>
                                <Routes>
                                  <Route path="/" element={<AhaMomentsHub />} />
                                  <Route path="/dashboard" element={<Dashboard />} />
                                  <Route path="/performance" element={<Performance />} />
                                  <Route path="/performance-analysis" element={<PerformanceAnalysis />} />
                                  <Route path="/coaching/:id" element={<CoachingArtifactDetail />} />
                                  <Route path="/explore" element={<Explore />} />
                                  <Route path="/viewer" element={<Viewer />} />
                                  <Route path="/lens-library" element={<LensLibrary />} />
                                  <Route path="/datasets" element={<DatasetManagement />} />
                                  <Route path="/dataset/:id" element={<DatasetDetail />} />
                                  <Route path="/account-analysis" element={<AccountAnalysisPage />} />
                                  <Route path="/account-settings" element={<AccountSettings />} />
                                  <Route path="/ai-transparency" element={<AITransparency />} />
                                </Routes>
                              </RouteErrorBoundary>
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                  </Router>
                </LayoutProvider>
              </PostHogUserIdentifier>
            </PostHogProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
};
